/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";
import axios from "axios";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// Authentication context
import { AuthProvider, useAuth } from "context/AuthContext";
import { RefundProgressProvider } from "context/RefundProgressContext";

// Offline detection service and component
import offlineService from "services/offlineService";
import NoInternet from "components/NoInternet";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
// import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
// import MDBox from "components/MDBox";


// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";
// import Configurator from "examples/Configurator";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
// Material Dashboard 2 React routes
import { getRoleBasedRoutes, SignIn } from "routes";

// User Layout for role-based rendering
import UserLayout from "layouts/fdo-panel/UserLayout";
import PasscodeAccess from "pages/PasscodeAccess";


// Protected Route component
// import ProtectedRoute from "components/ProtectedRoute";

// Material Dashboard 2 React contexts
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";

// Images
import brandWhite from "assets/images/namuve-logo.png";
import brandDark from "assets/images/namuve-logo.png";

const isBackendApi = (url) => {
  if (!url || typeof url !== 'string') return false;
  if (!url.includes('/api/')) return false;
  // Exclude external domains that happen to contain '/api/' in their URL
  if (url.includes('teable.namuve.com')) return false;
  if (url.includes('api.hostaway.com')) return false;
  if (url.includes('chat.team.namuve.com')) return false;
  return true;
};

// Global Axios Interceptor to auto-inject JWT token for backend API calls
axios.interceptors.request.use(
  (config) => {
    // Check both potential storage keys used in the app
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    
    // Only attach to our backend API calls, not external webhooks
    if (token && isBackendApi(config.url)) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Global Fetch Interceptor to auto-inject JWT token for backend API calls
const originalFetch = window.fetch;
window.fetch = async (...args) => {
  let [resource, config] = args;
  
  if (isBackendApi(resource)) {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    if (token) {
      config = config || {};
      config.headers = config.headers || {};
      // Handle Headers object or plain object
      if (config.headers instanceof Headers) {
        config.headers.set('Authorization', `Bearer ${token}`);
      } else {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }
  }
  
  return originalFetch(resource, config);
};

// Main App component with authentication
function AppContent() {
  // ALL HOOKS MUST BE CALLED FIRST - BEFORE ANY CONDITIONAL RETURNS
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    // layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const [isOnline, setIsOnline] = useState(offlineService.getStatus());
  const { pathname } = useLocation();
  const { user, isAuthenticated, loading } = useAuth();

  // Cache for the rtl - MUST be called before any conditional returns
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // Subscribe to offline/online status changes
  useEffect(() => {
    const unsubscribe = offlineService.subscribe((online) => {
      setIsOnline(online);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Check if current route is authentication page or passcode-access
  const isAuthPage = pathname.includes("/authentication/");
  const isPasscodeAccessPage = pathname.includes("/passcode-access");

  // Get role-based routes
  const roleBasedRoutes = getRoleBasedRoutes(user?.role, isAuthenticated, user?.permissions, user?.username);

  // Show loading while checking authentication
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
        }}
      >
        Loading...
      </div>
    );
  }

  // Show No Internet component when offline
  if (!isOnline) {
    return <NoInternet />;
  }

  // Open sidenav when mouse enter on mini sidenav
  // const handleOnMouseEnter = () => {
  //   if (miniSidenav && !onMouseEnter) {
  //     setMiniSidenav(dispatch, false);
  //     setOnMouseEnter(true);
  //   }
  // };

  // Close sidenav when mouse leave mini sidenav
  // const handleOnMouseLeave = () => {
  //   if (onMouseEnter) {
  //     setMiniSidenav(dispatch, true);
  //     setOnMouseEnter(false);
  //   }
  // };

  // Change the openConfigurator state
  // const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  const getRoutes = (allRoutes) => {
    return allRoutes.map((route) => {
      if (route.collapse && Array.isArray(route.collapse)) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route path={route.route} element={route.component} key={route.key} />;
      }
      return null;
    }).filter(Boolean);
  };

  // Determine default redirect based on authentication and role
  const getDefaultRedirect = () => {
    if (!isAuthenticated || !user) {
      return "/authentication/sign-in";
    }

    // If custom role, redirect to the first available authorized route
    if (user.role === "custom") {
      if (roleBasedRoutes && roleBasedRoutes.length > 0) {
        const firstValidRoute = roleBasedRoutes.find(
          (r) => r.route && r.route !== "/authentication/sign-in" && r.key !== "logout"
        );
        if (firstValidRoute) {
          return firstValidRoute.route;
        }
      }
      return "/upsells";
    }

    // All other authenticated users default to FDO Panel as home page
    return "/fdo-panel";
  };
  // Settings button hidden
  const configsButton = null;

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {/* Passcode Access - No sidebar, no authentication */}
        {isPasscodeAccessPage ? (
          <PasscodeAccess />
        ) : (
          <>
            {!isAuthPage && isAuthenticated && user?.role !== "user" && (
              <>
                <Sidenav
                  color={sidenavColor}
                  brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
                  brandName=""
                  routes={roleBasedRoutes}
                />
                {/* <Configurator /> */}
                {configsButton}
              </>
            )}
            {/* {layout === "vr" && <Configurator />} */}
            {user?.role === "user" && isAuthenticated && !isAuthPage ? (
              <UserLayout>
                <Routes>
                  {getRoutes(roleBasedRoutes)}
                  <Route path="/passcode-access" element={<PasscodeAccess />} />
                  <Route path="/" element={<Navigate to={getDefaultRedirect()} replace />} />
                  <Route path="*" element={<Navigate to={getDefaultRedirect()} replace />} />
                </Routes>
              </UserLayout>
            ) : (user?.role === "admin" || user?.role === "view_only" || user?.role === "custom") && isAuthenticated && !isAuthPage ? (
              <Routes>
                {getRoutes(roleBasedRoutes)}
                <Route path="/passcode-access" element={<PasscodeAccess />} />
                <Route path="/" element={<Navigate to={getDefaultRedirect()} replace />} />
                <Route path="*" element={<Navigate to={getDefaultRedirect()} replace />} />
              </Routes>
            ) : (
              <Routes>
                {getRoutes(roleBasedRoutes)}
                <Route path="/passcode-access" element={<PasscodeAccess />} />
                <Route path="/" element={<Navigate to={getDefaultRedirect()} replace />} />
                <Route path="*" element={<Navigate to={getDefaultRedirect()} replace />} />
              </Routes>
            )}
          </>
        )}
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {/* Passcode Access - No sidebar, no authentication */}
      {isPasscodeAccessPage ? (
        <PasscodeAccess />
      ) : (
        <>
          {!isAuthPage && isAuthenticated && user?.role !== "user" && (
            <>
              <Sidenav
                color={sidenavColor}
                brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
                brandName=""
                routes={roleBasedRoutes}
              />
              {/* <Configurator /> */}
              {configsButton}
            </>
          )}
          {/* {layout === "vr" && <Configurator />} */}
          {user?.role === "user" && isAuthenticated && !isAuthPage ? (
            <UserLayout>
              <Routes>
                {getRoutes(roleBasedRoutes)}
                <Route path="/passcode-access" element={<PasscodeAccess />} />
                <Route path="/" element={<Navigate to={getDefaultRedirect()} replace />} />
                <Route path="*" element={<Navigate to={getDefaultRedirect()} replace />} />
              </Routes>
            </UserLayout>
          ) : (user?.role === "admin" || user?.role === "view_only" || user?.role === "custom") && isAuthenticated && !isAuthPage ? (
            <Routes>
              {getRoutes(roleBasedRoutes)}
              <Route path="/passcode-access" element={<PasscodeAccess />} />
              <Route path="/" element={<Navigate to={getDefaultRedirect()} replace />} />
              <Route path="*" element={<Navigate to={getDefaultRedirect()} replace />} />
            </Routes>
          ) : (
            <Routes>
              {getRoutes(roleBasedRoutes)}
              <Route path="/passcode-access" element={<PasscodeAccess />} />
              <Route path="/" element={<Navigate to={getDefaultRedirect()} replace />} />
              <Route path="*" element={<Navigate to={getDefaultRedirect()} replace />} />
            </Routes>
          )}
        </>
      )}
    </ThemeProvider>
  );
}

// App wrapper with AuthProvider
export default function App() {
  return (
    <AuthProvider>
      <RefundProgressProvider>
        <AppContent />
      </RefundProgressProvider>
    </AuthProvider>
  );
}
